/* The input and dropdown component of the LanguageSelector. */
import React, { FC, useState, useRef } from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';
import useOnClickOutside from 'js/utils/hooks/useOnClickOutside';
import LanguageRow from 'js/recommendations/WorkFiltersCard/LanguageSelector/LanguageRow';
import { LanguageCode } from 'js/translation/util';

const TINY_SCREEN_SIZE = 630;

const Selector = styled.div`
  background-color: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 2px;
  height: 36px;
  position: relative;
  width: 248px;

  @media (max-width: ${TINY_SCREEN_SIZE}px) {
    width: 100%;
  }
`;

const Body = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  line-height: 19px;
  letter-spacing: -0.01em;
  color: #222222;
  padding: 0 12px;
  height: 36px;
  cursor: pointer;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 35px;
  width: 100%;
  background-color: #ffffff;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.24);
  border-radius: 0 0 4px 4px;
  display: flex;
  flex-direction: column;
  max-height: 325px;
  z-index: 1;
  overflow-y: scroll;
  padding: 6px 0;
`;

type Props = {
  selectedLanguage?: LanguageCode;
  onSelect: (code: LanguageCode) => void;
};

const languageNameMap: Record<LanguageCode, string> = {
  ar: 'Arabic',
  da: 'Danish',
  de: 'German',
  el: 'Greek, Modern',
  en: 'English',
  es: 'Spanish',
  fa: 'Persian',
  fi: 'Finnish',
  fr: 'French',
  he: 'Hebrew',
  hu: 'Hungarian',
  id: 'Indonesian',
  it: 'Italian',
  ko: 'Korean',
  nl: 'Dutch',
  no: 'Norwegian',
  pl: 'Polish',
  pt: 'Portuguese',
  ro: 'Romanian',
  ru: 'Russian',
  sv: 'Swedish',
  tr: 'Turkish',
  uk: 'Ukrainian',
  zh: 'Chinese',
};

// TODO: animate dropdown
const LanguageDropdown: FC<Props> = ({ selectedLanguage, onSelect }) => {
  // languages does not change
  const selectorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useOnClickOutside(selectorRef, () => isOpen && setIsOpen(false));

  return (
    <Selector
      ref={selectorRef}
      onClick={() => setIsOpen(!isOpen)}
      style={{
        borderColor: selectedLanguage ? '#dbdbdb' : '#094ec3',
      }}
    >
      <Body>
        {selectedLanguage
          ? languageNameMap[selectedLanguage]
          : 'Choose language...'}
        <Icon name="keyboard_arrow_down" color="#094ec3" />
      </Body>
      {isOpen && (
        <Dropdown>
          {Object.entries(languageNameMap).map(([code, name], i) => (
            <LanguageRow
              key={i}
              name={name}
              isSelected={selectedLanguage === code}
              onClick={() => onSelect(code as LanguageCode)}
            />
          ))}
        </Dropdown>
      )}
    </Selector>
  );
};

export default LanguageDropdown;
